import { Badge, Button, FilterChip, SignalDot } from '@indipia/ui-core'
import type { SignalLayer } from '../hooks/useSignalLayer'

// 간트·캘린더 툴바의 신호 컨트롤 — [신호등 켜기/끄기] · [빨강·노랑만 N] · 상단바 [오늘 할일 N] · 오류 표시.
// 서버 신호·할일 조회가 실패하면(SIGNAL_MISSING 503 포함) 점·건수 대신 명시적 오류를 보여준다(폴백 없음).

/** 옵션 바 칩 묶음: [신호등 켜기/끄기] + [빨강·노랑만 N](꺼짐이면 비활성 노출) + 신호 조회 오류 표시 */
export function SignalToggleChips({ layer, size = 'lg' }: { layer: SignalLayer; size?: 'sm' | 'md' | 'lg' }) {
  const { signalOn, toggleSignal, unavailable, onlyRY, setOnlyRY, ryTaskIds, signalsQ } = layer
  return (
    <>
      <FilterChip
        size={size}
        active={signalOn}
        onClick={toggleSignal}
        aria-pressed={signalOn}
        // 현장 종료(거래완료) 스케줄은 신호 조회 대상이 아니라 켤 수 없다 — 비활성 칩 + 사유 안내
        disabled={unavailable}
        title={unavailable ? '거래완료된 현장은 신호등을 켤 수 없어요' : undefined}
      >
        <SignalDot color="RED" size={8} />
        {signalOn ? '신호등 끄기' : '신호등 켜기'}
      </FilterChip>
      {!signalOn && (
        <FilterChip size={size} active={false} disabled aria-pressed={false}>
          빨강·노랑만
        </FilterChip>
      )}
      {signalOn && !signalsQ.isError && (
        <FilterChip size={size} active={onlyRY} count={ryTaskIds.size} onClick={() => setOnlyRY(!onlyRY)} aria-pressed={onlyRY}>
          빨강·노랑만
        </FilterChip>
      )}
      {signalOn && signalsQ.isError && (
        <span role="alert" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--status-danger-600)' }}>
          신호 정보를 불러오지 못했어요
          <button
            type="button"
            onClick={() => { void signalsQ.refetch() }}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, color: 'var(--status-danger-600)', textDecoration: 'underline', padding: 0 }}
          >다시 시도</button>
        </span>
      )}
    </>
  )
}

/**
 * 상단바 [오늘 할일 N] — 0건이면 숨긴다. 조회 오류면 건수 대신 오류 버튼(누르면 패널에서 다시 시도).
 * onOpen 에 버튼 위치(viewport 좌표)를 넘긴다 — 패널이 그 아래에 뜬다.
 */
export function TodayTodoButton({ count, isError, isLoading, onOpen, size = 'md' }: {
  count: number
  isError: boolean
  isLoading: boolean
  onOpen: (anchor: { x: number; y: number }) => void
  size?: 'sm' | 'md'
}) {
  if (!isError && (isLoading || count === 0)) return null
  return (
    <Button
      variant="secondary"
      size={size}
      className="gap-1"
      style={isError ? { color: 'var(--status-danger-600)', borderColor: 'var(--status-danger-600)' } : undefined}
      onClick={e => {
        e.stopPropagation()
        const r = e.currentTarget.getBoundingClientRect()
        onOpen({ x: r.left, y: r.bottom + 6 })
      }}
    >
      {isError ? '오늘 할일 오류' : (
        <>
          오늘 할일
          <Badge status="danger">{count}</Badge>
        </>
      )}
    </Button>
  )
}
